import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExclamationCircleFilled } from '@ant-design/icons';
import {
    BaseAlertBanner as AlertBanner,
    BaseButton,
    BaseDivider,
    BaseInput as Input,
    BaseRow,
    LocaleSwitcher,
} from '@components';
import { HintText, InputLabel, InputWrapper } from '@components/input/styled.tsx';
import ClientLogo from '@components/logo';
import { ALERT_STATUSES, AlertNotifications } from '@constants/alertNotifications.ts';
import { INTERNAL_SERVER_ERROR_STATUS_CODE } from '@constants/api.ts';
import { TEST_IDS } from '@constants/testIds.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import FooterLogo from '@layouts/auth/footer-logo';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import { onTokensUpdated } from '@routes/auth/___shared___/authTokenRefreshScheduler.ts';
import { getFieldError, getUpdatedFormFieldsWithErrors, isFieldErroneous } from '@routes/auth/___shared___/helpers.ts';
import ResetPasswordModal from '@routes/auth/___shared___/reset-password-modal';
import { renderChangePasswordInput } from '@routes/auth/___shared___/utils.tsx';
import type { LoginErrorResponse } from '@routes/auth/login/___shared___/types.ts';
import {
    SHARED_LOGIN_EMAIL_REQUIRE,
    SHARED_LOGIN_EMAIL_VALID_ERROR,
    SHARED_LOGIN_FORGOT_PASSWORD,
    SHARED_LOGIN_INCORRECT,
    SHARED_LOGIN_LOCKED_USER_DESC,
    SHARED_LOGIN_LOCKED_USER_DESC_LINK,
    SHARED_LOGIN_PASSWORD_REQUIRED,
} from '@routes/auth/login/constants.ts';
import { doLogin, setEmail, setError } from '@routes/auth/login/model/login.slice.ts';
import { ConsentModal } from '@routes/auth/login/ui/consent-agreement-modal';
import { START_PAGES_OPTIONS } from '@routes/sidebar/settings/ui/user-preferences-tab/constants.ts';
import { clearAlert, setAlert } from '@shared/model/alert.slice.ts';
import { deleteLockUserTime, persistLockUserData } from '@store/services/localStorage.service.ts';
import type { FormField } from '@types';
import { isTemporarilyLocked, renderAlertErrorDesc } from '@utils/general.tsx';
import { getCallbackUrl } from '@utils/navigation.ts';
import { translateItem } from '@utils/translations.ts';
import { isEmailValid } from '@utils/validation.ts';
import { Form } from 'antd';
import { orderBy } from 'lodash';

import type { UserInfoConsent } from '../../___shared___/user.api.ts';
import { getFullUserInfo } from '../../___shared___/user.slice.ts';
import {
    AuthSharedWrapper,
    FooterLogoSection,
    FormSection,
    InvalidEmailErrorMsg,
    PasswordSection as ForgotPassword,
} from '../___shared___/styled.tsx';
import {
    FailedAttemptsSection,
    LockedUserTitle,
    LockedUserTitleContact,
    LockedUserWrapper,
    TopAlertBannerSection,
} from './styled.tsx';

const LOGIN_FIELD_NAME = 'Email';
const PASSWORD_FIELD_NAME = 'password';

const isLandingPageAvailableAsStartPage = (landingPage: string) =>
    !!START_PAGES_OPTIONS.find((option) => option.value === landingPage);

const getStartPage = (landingPage: string) =>
    isLandingPageAvailableAsStartPage(landingPage) ? landingPage : ROUTES.SIDEBAR.LEARNING;

const getUpdatedFields = (err: LoginErrorResponse, formFields: FormField[]) => {
    if (!err) return;

    const mappedErrors =
        err?.data?.reason?.errorFields?.length > 0
            ? err.data.reason.errorFields.map((error) => ({
                  name: error.name,
                  message: error.error,
              }))
            : [];

    return getUpdatedFormFieldsWithErrors(formFields, mappedErrors).updatedFields;
};

const Login: React.FC = () => {
    const { t } = useLingui();
    const LOCKED_USER_DESC = translateItem(SHARED_LOGIN_LOCKED_USER_DESC);
    const LOCKED_USER_DESC_LINK = translateItem(SHARED_LOGIN_LOCKED_USER_DESC_LINK);
    const EMAIL_REQUIRE_MSG = translateItem(SHARED_LOGIN_EMAIL_REQUIRE);
    const EMAIL_VALID_ERROR_MSG = translateItem(SHARED_LOGIN_EMAIL_VALID_ERROR);
    const PASSWORD_REQUIRED_MSG = translateItem(SHARED_LOGIN_PASSWORD_REQUIRED);

    const navigate = useCustomNavigate();
    const dispatch = useAppDispatch();

    const error = useAppSelector((state) => state.authLogin.error);
    const { loginAttemps, maxLoginAttemps } = error?.data || {};

    const [isPasswordType, setIsPasswordType] = useState({ [PASSWORD_FIELD_NAME]: true });
    const [isLoading, setLoading] = useState(false);
    const [formFields, setFormFields] = useState<FormField[]>([]);
    const [formHasErrors, setFormHasErrors] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [userInfoConsents, setUserInfoConsents] = useState<UserInfoConsent[]>([]);
    const [rootPage, setRootPage] = useState('');
    const [isDeactivated, setIsDeactivated] = useState(false);
    const [isRegistered, setIsRegistered] = useState(true);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isValidLogin, setIsValidLogin] = useState(true);

    const [form] = Form.useForm();
    const loginHasError = isFieldErroneous(LOGIN_FIELD_NAME, formFields);
    const loginFieldValue = Form.useWatch(LOGIN_FIELD_NAME, form);
    const [searchParams, _] = useSearchParams();

    const CLIENT_FIELD_ERRORS = new Set([EMAIL_REQUIRE_MSG, EMAIL_VALID_ERROR_MSG]);

    const hasFormValidationError = formHasErrors && !loginHasError && !isDeactivated;

    const onFieldsChange = (_: any[], allFields: any[]) => {
        // AntD Form.Item names are stored as an array of path segments, so f.name is e.g. ['login']
        const loginField = allFields.find((f) => f.name?.[0] === LOGIN_FIELD_NAME);
        const firstError = loginField?.errors?.[0] ?? '';

        const isClientError = CLIENT_FIELD_ERRORS.has(firstError);

        setFormHasErrors(!isClientError && Boolean(firstError));
        setFormFields(allFields as FormField[]);
    };

    const handleSubmit = () => {
        form.validateFields().then(({ Email, password }) => {
            setLoading(true);
            dispatch(doLogin({ email: Email, password }))
                .then((res) => {
                    deleteLockUserTime();
                    onTokensUpdated(res.accessToken, res.refreshTokenExpiresAtUTC, dispatch);
                    return dispatch(getFullUserInfo());
                })
                .then((userInfoData) => {
                    const consents = userInfoData?.consents || [];
                    const sortedConsents = orderBy(consents, 'approvedDateUtc', 'desc');
                    const landingPage = userInfoData?.preferences?.landingPage || '';
                    const hasPending = consents?.some((c) => !c.isApproved);
                    setUserInfoConsents(sortedConsents);
                    setRootPage(landingPage);
                    setLoading(false);
                    dispatch(setEmail(Email));

                    if (hasPending) {
                        setModalOpen(true);
                    } else {
                        const callbackUrl = getCallbackUrl(searchParams);
                        const startPage = callbackUrl ?? getStartPage(landingPage);
                        navigate(startPage, { state: { from: ROUTES.AUTH.LOGIN } });
                    }
                })
                .catch((err) => {
                    const attemptedEmail = form.getFieldValue(LOGIN_FIELD_NAME);

                    dispatch(setEmail(attemptedEmail));

                    if (err?.data?.isDeactivated) {
                        setIsDeactivated(true);
                    }

                    if (!err.data?.isRegistered) {
                        setIsRegistered(false);
                    }

                    if (err.status < INTERNAL_SERVER_ERROR_STATUS_CODE) {
                        dispatch(clearAlert()); //skip banner
                    } else {
                        dispatch(
                            setAlert({
                                type: ALERT_STATUSES.ERROR,
                                message: AlertNotifications[ALERT_STATUSES.ERROR].message,
                                description: renderAlertErrorDesc(err.status, err.statusText),
                            }),
                        );
                    }

                    if (isTemporarilyLocked(attemptedEmail)) {
                        setLoading(false);
                        dispatch(setError(null));
                        navigate(ROUTES.AUTH.TEMP_LOCKED_ACCOUNT);
                        return;
                    }

                    if (err?.data?.loginAttemps !== undefined && err?.data?.maxLoginAttemps !== undefined) {
                        const isAccountLocked = err.data.loginAttemps >= err.data.maxLoginAttemps;

                        if (isAccountLocked && err.data.lockTimeEndUtc) {
                            persistLockUserData(err.data.lockTimeEndUtc, attemptedEmail);
                            setLoading(false);
                            dispatch(setError(null));
                            navigate(ROUTES.AUTH.TEMP_LOCKED_ACCOUNT);
                            return;
                        }
                    }

                    const updatedFields = getUpdatedFields(err, formFields);
                    setFormFields(updatedFields || formFields);
                    form.setFieldValue(PASSWORD_FIELD_NAME, '');
                    setFormHasErrors(true);
                    setLoading(false);
                });
        });
    };

    const handleContactAdmin = () => {
        // TODO: add correct page
    };

    return (
        <>
            {!!hasFormValidationError &&
                (isRegistered ? (
                    <TopAlertBannerSection>
                        <AlertBanner
                            title={translateItem(SHARED_LOGIN_INCORRECT)}
                            description={t`Please check your credentials and try again. Your account will be temporarily locked after 3 failed attempts.`}
                            type="error"
                        />
                    </TopAlertBannerSection>
                ) : (
                    <TopAlertBannerSection>
                        <AlertBanner
                            title={t`Registration required`}
                            description={t`We sent you a registration link. Please check your email and complete your registration before logging in.`}
                            type="warning"
                        />
                    </TopAlertBannerSection>
                ))}

            {!!isDeactivated && (
                <TopAlertBannerSection>
                    <AlertBanner
                        description={
                            <LockedUserWrapper>
                                <LockedUserTitle>{LOCKED_USER_DESC}</LockedUserTitle>
                                <LockedUserTitleContact onClick={handleContactAdmin}>
                                    {LOCKED_USER_DESC_LINK}
                                </LockedUserTitleContact>
                            </LockedUserWrapper>
                        }
                        type="warning"
                    />
                </TopAlertBannerSection>
            )}

            {!!userInfoConsents && (
                <ConsentModal
                    userInfoConsents={userInfoConsents}
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    redirectUrl={rootPage}
                />
            )}

            <AuthSharedWrapper>
                <FormSection>
                    <ClientLogo />

                    <FailedAttemptsSection $isVisible={loginAttemps! > 0}>
                        {t`Failed attempt: ${loginAttemps} of ${maxLoginAttemps}`}
                    </FailedAttemptsSection>

                    <Form form={form} onFinish={handleSubmit} onFieldsChange={onFieldsChange}>
                        <InputWrapper>
                            <InputLabel>{t`Login`}</InputLabel>
                            <Form.Item
                                noStyle
                                name={LOGIN_FIELD_NAME}
                                validateTrigger={['onBlur']}
                                rules={[
                                    { required: true, message: EMAIL_REQUIRE_MSG },
                                    { type: 'email', message: EMAIL_VALID_ERROR_MSG },
                                ]}
                            >
                                <Input
                                    placeholder={t`Enter your registered email`}
                                    suffixIcon={loginHasError ? <ExclamationCircleFilled /> : null}
                                    hasError={loginHasError}
                                    data-cy={TEST_IDS.AUTH.LOGIN_INPUT}
                                />
                            </Form.Item>
                            <HintText $showByDefault={loginHasError} className="has-error">
                                {getFieldError(LOGIN_FIELD_NAME, formFields)}
                            </HintText>
                        </InputWrapper>

                        {renderChangePasswordInput(
                            PASSWORD_REQUIRED_MSG,
                            PASSWORD_FIELD_NAME,
                            PASSWORD_FIELD_NAME,
                            t`Password`,
                            t`Enter your password`,
                            formFields,
                            isPasswordType,
                            setIsPasswordType,
                        )}

                        <Form.Item noStyle>
                            <BaseButton
                                className="full-width"
                                htmlType="submit"
                                loading={isLoading}
                                $customType="primary"
                                data-cy={TEST_IDS.AUTH.LOGIN_BUTTON}
                            >
                                {t`Log in`}
                            </BaseButton>
                        </Form.Item>
                    </Form>

                    <BaseDivider />

                    <BaseRow justify="space-between" align="middle" style={{ width: '100%' }}>
                        <BaseRow align="middle" justify="start">
                            <LocaleSwitcher />
                        </BaseRow>
                        <BaseRow align="middle" justify="end">
                            <ForgotPassword
                                onClick={() => {
                                    const isLoginValid = isEmailValid(form.getFieldValue(LOGIN_FIELD_NAME));
                                    setIsValidLogin(isLoginValid);

                                    if (isLoginValid) {
                                        setIsResetModalOpen(true);
                                    }
                                }}
                            >
                                {translateItem(SHARED_LOGIN_FORGOT_PASSWORD)}
                            </ForgotPassword>
                        </BaseRow>
                    </BaseRow>
                    {!isValidLogin && <InvalidEmailErrorMsg>{t`Provide a valid login`}</InvalidEmailErrorMsg>}

                    <ResetPasswordModal
                        open={isResetModalOpen}
                        email={loginFieldValue}
                        onClose={() => setIsResetModalOpen(false)}
                    />
                </FormSection>

                <FooterLogoSection>
                    <FooterLogo />
                </FooterLogoSection>
            </AuthSharedWrapper>
        </>
    );
};

export default Login;
